import React, { useEffect, useState } from "react";
import { getAllBanks } from "../../../../../api/onboarddeboard";

const inputStyle = {
  border: "1.5px solid #e2e8f0",
  borderRadius: 8,
  padding: "10px 14px",
  fontSize: 14,
  color: "#1a202c",
  background: "#f8fafc",
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
  fontFamily: "inherit",
};

const labelStyle = {
  fontWeight: 600,
  fontSize: 13,
  color: "#374151",
  marginBottom: 6,
  display: "block",
};

const errorStyle = { color: "#dc3545", fontSize: 12, marginTop: 4 };

const BankDetails = ({
  employeeData,
  setEmployeeData,
  errors = {},
  setErrors,
  touchedFields = {},
  setTouchedFields,
}) => {
  const token = localStorage.getItem("token");
const [bankOptions, setBankOptions] = useState([]);
  const bankSelected = !!employeeData.bankDetails?.bankId;
 const [pendingBank, setPendingBank] = useState(null); // { name, id } of bank awaiting confirmation
  const [showBankChangeConfirm, setShowBankChangeConfirm] = useState(false);
  const [showClearedAlert, setShowClearedAlert] = useState(false);
  useEffect(() => {
    const fetchBanks = async () => {
      try {
        const res = await getAllBanks(token);
        if (res.status === 200) {
          let active = res.data.filter((b) => (b.status || "Active") === "Active");

          // If this employee's saved bank is no longer active, still show it
          // in the list (so the dropdown reflects the real saved state)
          const savedBankId = employeeData.bankDetails?.bankId;
          const savedBankName = employeeData.bankDetails?.bankName;
          const alreadyInList = active.some((b) => b.id === savedBankId);
          if (savedBankId && savedBankName && !alreadyInList) {
            active = [...active, { id: savedBankId, bankName: `${savedBankName} (Inactive)`, status: "Active" }];
          }

          setBankOptions(active);
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchBanks();
  }, [token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEmployeeData((prev) => ({
      ...prev,
      bankDetails: { ...prev.bankDetails, [name]: value },
    }));
  };

const handleBankSelect = (e) => {
    const selectedName = e.target.value;
    const matchedBank = bankOptions.find((b) => b.bankName === selectedName);
    const currentBankId = employeeData.bankDetails?.bankId;

    // Switching from one real bank to a different real bank
    if (bankSelected && matchedBank && matchedBank.id !== currentBankId) {
      setPendingBank({ name: selectedName, id: matchedBank.id });
      setShowBankChangeConfirm(true);
      return;
    }

    // Resetting back to "Select bank" when a bank was already selected
    if (bankSelected && !matchedBank) {
      setPendingBank({ name: "", id: null });
      setShowBankChangeConfirm(true);
      return;
    }

    setEmployeeData((prev) => ({
      ...prev,
      bankDetails: {
        ...prev.bankDetails,
        bankName: selectedName,
        bankId: matchedBank ? matchedBank.id : null,
      },
    }));
  };

const confirmBankChange = () => {
    if (!pendingBank) return;
    const isClearingBank = pendingBank.id === null;

    setEmployeeData((prev) => ({
      ...prev,
      bankDetails: {
        ...prev.bankDetails,
        bankName: pendingBank.name,
        bankId: pendingBank.id,
        accountNo: "",
        ifscCode: "",
        branchName: "",
        pan_No: "",
        uan_No: "",
      },
    }));
    setErrors((prev) => {
      const { accountNo, pan_No, uan_No, ...rest } = prev;
      return rest;
    });
    setPendingBank(null);
    setShowBankChangeConfirm(false);

    if (isClearingBank) {
      setShowClearedAlert(true);
      setTimeout(() => setShowClearedAlert(false), 3000);
    }
  };

  const cancelBankChange = () => {
    setPendingBank(null);
    setShowBankChangeConfirm(false);
  };

  const handleAccountNoChange = (e) => {
    const value = e.target.value;
    setEmployeeData((prev) => ({ ...prev, bankDetails: { ...prev.bankDetails, accountNo: value } }));
    const t = value.trim();
    if (value === "") { setErrors((prev) => ({ ...prev, accountNo: "" })); return; }
    if (/\s/.test(value)) { setErrors((prev) => ({ ...prev, accountNo: "Account number cannot contain spaces" })); return; }
    if (!/^[0-9]+$/.test(t)) { setErrors((prev) => ({ ...prev, accountNo: "Account number must contain only digits" })); return; }
    if (t.length < 8) { setErrors((prev) => ({ ...prev, accountNo: "Account number must be at least 8 digits" })); return; }
    if (t.length > 20) { setErrors((prev) => ({ ...prev, accountNo: "Account number cannot be more than 20 digits" })); return; }
    setErrors((prev) => { const { accountNo, ...r } = prev; return r; });
  };

  const handlePanChange = (e) => {
    const value = e.target.value.toUpperCase();
    setEmployeeData((prev) => ({ ...prev, bankDetails: { ...prev.bankDetails, pan_No: value } }));
    const t = value.trim();
    if (value === "") { setErrors((prev) => { const { pan_No, ...r } = prev; return r; }); return; }
    if (/\s/.test(value)) { setErrors((prev) => ({ ...prev, pan_No: "PAN cannot contain spaces" })); return; }
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(t)) { setErrors((prev) => ({ ...prev, pan_No: "Enter valid PAN (Example: ABCDE1234F)" })); return; }
    setErrors((prev) => { const { pan_No, ...r } = prev; return r; });
  };

  const handleUanChange = (e) => {
    const value = e.target.value;
    setEmployeeData((prev) => ({ ...prev, bankDetails: { ...prev.bankDetails, uan_No: value } }));
    const t = value.trim();
    if (value === "") { setErrors((prev) => { const { uan_No, ...r } = prev; return r; }); return; }
    if (/\s/.test(value)) { setErrors((prev) => ({ ...prev, uan_No: "UAN cannot contain spaces" })); return; }
    if (!/^[0-9]+$/.test(t)) { setErrors((prev) => ({ ...prev, uan_No: "UAN must contain only digits" })); return; }
    if (t.length < 12) { setErrors((prev) => ({ ...prev, uan_No: "UAN must be exactly 12 digits" })); return; }
    if (t.length > 12) { setErrors((prev) => ({ ...prev, uan_No: "UAN cannot be more than 12 digits" })); return; }
    setErrors((prev) => { const { uan_No, ...r } = prev; return r; });
  };

  const accountHolderName = [
    employeeData.personalDetails?.firstName,
    employeeData.personalDetails?.lastName,
  ].filter(Boolean).join(" ");

  useEffect(() => {
    // Only auto-fill when the field is empty (new employee flow).
    // Never overwrite an already-saved accountHolderName just because it
    // doesn't exactly match firstName+lastName — that was silently mutating
    // edit-mode data before the user touched anything.
    if (accountHolderName && !employeeData.bankDetails?.accountHolderName) {
      setEmployeeData((prev) => ({
        ...prev,
        bankDetails: { ...prev.bankDetails, accountHolderName },
      }));
    }
  }, [accountHolderName]);

  return (
    <>
      {/* ROW 1 — Bank Name (text) | Account Holder Name (auto-filled, readonly) */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 18 }}>
      <div>
          <label style={labelStyle}>Bank Name</label>
          <select
            name="bankName"
            value={employeeData.bankDetails?.bankName || ""}
            onChange={handleBankSelect}
            style={{ ...inputStyle, borderColor: errors.bankName ? "#dc3545" : "#e2e8f0" }}
          >
            <option value="">Select bank</option>
            {bankOptions.map((bank) => (
              <option key={bank.id} value={bank.bankName}>
                {bank.bankName}
              </option>
            ))}
          </select>
          {errors.bankName && <div style={errorStyle}>{errors.bankName}</div>}
        </div>
        <div>
          <label style={labelStyle}>Account Holder Name</label>
          <input
            type="text"
            placeholder="Account holder"
            value={accountHolderName}
            readOnly
            style={{ ...inputStyle, background: "#f0f2f5", color: "#9ca3af", cursor: "not-allowed" }}
          />
        </div>
      </div>

      {/* ROW 2 — Account Number | IFSC Code */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 18 }}>
        <div>
          <label style={labelStyle}>Account Number</label>
          <input
            type="text"
            name="accountNo"
            placeholder="Enter account number"
            value={employeeData.bankDetails?.accountNo || ""}
            onChange={handleAccountNoChange}
            disabled={!bankSelected}
            style={{
              ...inputStyle,
              borderColor: errors.accountNo ? "#dc3545" : "#e2e8f0",
              ...(!bankSelected ? { background: "#f0f2f5", color: "#9ca3af", cursor: "not-allowed" } : {}),
            }}
          />
          {errors.accountNo && <div style={errorStyle}>{errors.accountNo}</div>}
        </div>
        <div>
          <label style={labelStyle}>IFSC Code</label>
          <input
            type="text"
            name="ifscCode"
            placeholder="Enter IFSC Code"
            value={employeeData.bankDetails?.ifscCode || ""}
            onChange={handleChange}
            disabled={!bankSelected}
            style={!bankSelected ? { ...inputStyle, background: "#f0f2f5", color: "#9ca3af", cursor: "not-allowed" } : inputStyle}
          />
        </div>
      </div>

      {/* ROW 3 — Branch (half width) */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 18 }}>
        <div>
          <label style={labelStyle}>Branch</label>
          <input
            type="text"
            name="branchName"
            placeholder="Branch name"
            value={employeeData.bankDetails?.branchName || ""}
            onChange={handleChange}
            disabled={!bankSelected}
            style={!bankSelected ? { ...inputStyle, background: "#f0f2f5", color: "#9ca3af", cursor: "not-allowed" } : inputStyle}
          />
        </div>
      </div>

      {/* ROW 4 — PAN Number | UAN Number */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 18 }}>
        <div>
          <label style={labelStyle}>PAN Number</label>
          <input
            type="text"
            name="pan_No"
            placeholder="Enter PAN number"
          value={employeeData.bankDetails?.pan_No || ""}
            onChange={handlePanChange}
            disabled={!bankSelected}
            style={{
              ...inputStyle,
              borderColor: errors.pan_No ? "#dc3545" : "#e2e8f0",
              ...(!bankSelected ? { background: "#f0f2f5", color: "#9ca3af", cursor: "not-allowed" } : {}),
            }}
          />
          {errors.pan_No && <div style={errorStyle}>{errors.pan_No}</div>}
        </div>
        <div>
          <label style={labelStyle}>UAN Number</label>
          <input
            type="text"
            name="uan_No"
            placeholder="Enter UAN number"
            value={employeeData.bankDetails?.uan_No || ""}
            onChange={handleUanChange}
            disabled={!bankSelected}
            style={{
              ...inputStyle,
              borderColor: errors.uan_No ? "#dc3545" : "#e2e8f0",
              ...(!bankSelected ? { background: "#f0f2f5", color: "#9ca3af", cursor: "not-allowed" } : {}),
            }}
          />
         {errors.uan_No && <div style={errorStyle}>{errors.uan_No}</div>}
        </div>
      </div>

      {showBankChangeConfirm && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.45)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 2000,
          }}
        >
          <div
            style={{
              background: "#fff", borderRadius: 12, padding: "24px 28px",
              width: "100%", maxWidth: 420, boxShadow: "0 10px 40px rgba(0,0,0,0.18)",
              fontFamily: "inherit",
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 16, color: "#111827", marginBottom: 10 }}>
              Change bank?
            </div>
          <div style={{ fontSize: 14, color: "#4b5563", lineHeight: 1.5, marginBottom: 22 }}>
              {pendingBank?.id === null
                ? "Are you sure you want to remove the selected bank? All previously entered bank details (account number, IFSC, branch, PAN, UAN) will be permanently cleared."
                : "Are you sure you want to change the bank? All previously entered bank details (account number, IFSC, branch, PAN, UAN) will be reset."}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button
                onClick={cancelBankChange}
                style={{
                  padding: "9px 18px", borderRadius: 8, border: "1.5px solid #e2e8f0",
                  background: "#fff", color: "#374151", fontWeight: 600, fontSize: 13,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmBankChange}
                style={{
                  padding: "9px 18px", borderRadius: 8, border: "none",
                  background: "#2E5CB8", color: "#fff", fontWeight: 600, fontSize: 13,
                  cursor: "pointer",
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {showClearedAlert && (
        <div
          style={{
            position: "fixed", top: 24, left: "50%", transform: "translateX(-50%)",
            background: "#111827", color: "#fff", padding: "12px 22px",
            borderRadius: 8, fontSize: 13, fontWeight: 500,
            boxShadow: "0 8px 24px rgba(0,0,0,0.25)", zIndex: 2100,
          }}
        >
          All bank details have been cleared.
        </div>
      )}
    </>
  );
};

export default BankDetails;
